import React from "react";
import {
  DropdownInput,
  IOption,
  IAlertMessageProps,
  IPopupProps,
  IMultiSelectItem,
  MultiSelectList,
  QuestionTooltipProps,
} from "component-library";

import {
  withGlobalContext,
  IGlobalContextProps,
  GlobalContextEventTypes,
} from "../../../../../../contexts/GlobalContext";
import { WarningIcon } from "assets";
import { GlobalAlertMessage } from "../../../../../Common/GlobalAlert";
import {
  IAppearanceViewModel,
  FacetViewModel,
  FacetsViewModel,
} from "../../../../models/views";
import { ControlSection } from "../../../../../Common/ControlSection";
import { PromiseContainer } from "../../../../../../utils/promise";

export interface IAppearanceContent {
  appearanceSectionName: string;
  appearanceDescription: string;
  appearanceTooltip: QuestionTooltipProps;
  appearanceField: string;
  appearancePlaceholder: string;
  eyeColorField: string;
  eyeColorPlaceholder: string;
  hairColorField: string;
  hairColorPlaceholder: string;
  hairLengthField: string;
  hairLengthPlaceholder: string;
  facialHairField: string;
  facialHairPlaceholder: string;
  appearanceWarning: {
    title: string;
    description: string;
    close: string;
    add: string;
  };
}

interface IAppearanceProps extends IGlobalContextProps {
  model: IAppearanceViewModel;
  facets: FacetsViewModel;
  content: IAppearanceContent;
  handleChanges: (model: IAppearanceViewModel) => void;
}

interface IAppearanceState {
  selectedAppearances: IMultiSelectItem<number>[];
  isDisabledAppearance: boolean;
}

export class Appearance extends React.Component<
  IAppearanceProps,
  IAppearanceState
> {
  private approveAddingPromise: PromiseContainer<undefined> =
    new PromiseContainer<undefined>();
  private maxAppearanceActiveCount: number;

  constructor(props: IAppearanceProps) {
    super(props);

    this.maxAppearanceActiveCount = props.facets.appearances.maxActiveCount;

    this.state = {
      isDisabledAppearance:
        props.model.appearances.length >= this.maxAppearanceActiveCount,
      selectedAppearances: props.model.appearances.map((appearance) => ({
        name: appearance.name,
        value: appearance.id,
      })),
    };
  }

  public render() {
    const { selectedAppearances, isDisabledAppearance } = this.state;
    const {
      content,
      facets: {
        appearances: { facets: appearanceFacets },
        ...facets
      },
    } = this.props;

    return (
      <ControlSection
        name={content.appearanceSectionName}
        description={content.appearanceDescription}
      >
        <div className="c-additional-info-form__appearance">
          <div className="c-additional-info-form__appearance-list">
            <MultiSelectList
              type="dropdown"
              content={{
                label: content.appearanceField,
                placeholder: content.appearancePlaceholder,
                tooltip: content.appearanceTooltip,
              }}
              isDisabled={isDisabledAppearance}
              name="appearance"
              selectedItems={selectedAppearances}
              list={this.mapAppearances(appearanceFacets)}
              handleChanges={this.handleAppearanceChanges}
            />
          </div>
          <div className="c-additional-info-form__separator" />
          <div className="c-additional-info-form__control-grid-2">
            <div className="c-additional-info-form__control-grid-cell">
              {this.renderDropDown("eyeColor", facets.eyeColors.facets, {
                label: content.eyeColorField,
                placeholder: content.eyeColorPlaceholder,
              })}
            </div>
            <div className="c-additional-info-form__control-grid-cell">
              {this.renderDropDown("hairColor", facets.hairColors.facets, {
                label: content.hairColorField,
                placeholder: content.hairColorPlaceholder,
              })}
            </div>
            <div className="c-additional-info-form__control-grid-cell">
              {this.renderDropDown("hairLength", facets.hairLengths.facets, {
                label: content.hairLengthField,
                placeholder: content.hairLengthPlaceholder,
              })}
            </div>
            <div className="c-additional-info-form__control-grid-cell">
              {this.renderDropDown("facialHair", facets.facialHairs.facets, {
                label: content.facialHairField,
                placeholder: content.facialHairPlaceholder,
              })}
            </div>
          </div>
        </div>
      </ControlSection>
    );
  }

  // eslint-disable-next-line react/no-deprecated
  public componentWillReceiveProps(nextProps: IAppearanceProps) {
    if (nextProps.model.appearances !== this.props.model.appearances) {
      this.setState({
        selectedAppearances: nextProps.model.appearances.map((appearance) => ({
          name: appearance.name,
          value: appearance.id,
        })),
      });
    }
  }

  private renderDropDown = (
    name: string,
    facets: FacetViewModel[],
    text: { label: string; placeholder: string }
  ) => {
    const options: IOption<number>[] = [
      this.getEmptyNumberOption(text.placeholder),
      ...facets.map((facet) => ({ name: facet.name, value: facet.id })),
    ];

    return (
      <DropdownInput<number>
        id={`appearance${name}`}
        variant="select"
        options={options}
        name={name}
        value={this.props.model[name]}
        {...text}
        valueChanged={this.dropdownChanged(name)}
      />
    );
  };

  private handleAppearanceChanges = async (
    selectedList: IMultiSelectItem<number>[]
  ) => {
    if (
      selectedList.length > 3 &&
      selectedList.length > this.state.selectedAppearances.length
    ) {
      this.props.globalContext.notifyListener(
        GlobalContextEventTypes.notifyingGlobalAlert,
        this.getAlertMessage()
      );

      this.approveAddingPromise.setPromise();

      await this.approveAddingPromise.promise;

      this.updateModel(selectedList);
      this.props.globalContext.notifyListener(
        GlobalContextEventTypes.closeAllGlobalAlert
      );
    } else {
      this.updateModel(selectedList);
    }
  };

  private updateModel = (selectedList: IMultiSelectItem<number>[]) => {
    const updatedModel = { ...this.props.model };
    const updatedState = { ...this.state };

    updatedState.isDisabledAppearance =
      selectedList.length >= this.maxAppearanceActiveCount;
    updatedState.selectedAppearances = selectedList;
    updatedModel.appearances = selectedList.map((item) => ({
      id: item.value,
      name: item.name,
    }));

    this.setState(updatedState, () => this.props.handleChanges(updatedModel));
  };

  private getEmptyNumberOption = (placeholder: string): IOption<number> => ({
    value: NaN,
    name: placeholder,
  });

  private dropdownChanged = (fieldName: string) => {
    return (value: any) => {
      const updatedModel = { ...this.props.model };

      updatedModel[fieldName] = value;

      if (updatedModel[fieldName] !== this.props.model[fieldName]) {
        this.props.handleChanges(updatedModel);
      }
    };
  };

  private mapAppearances = (appearances: FacetViewModel[]) =>
    appearances.map((item) => ({ name: item.name, value: item.id }));

  private getAlertMessage = () => {
    const {
      content: { appearanceWarning },
    } = this.props;

    const alertProps: IAlertMessageProps = {
      icon: <WarningIcon />,
      texts: {
        title: appearanceWarning.title,
        description: appearanceWarning.description,
      },
      buttons: [
        {
          name: appearanceWarning.add,
          click: () => this.approveAddingPromise.resolve(),
          type: "primary",
        },
      ],
    };

    const popupProps: IPopupProps = {
      width: { lg: 4, md: 6 },
      close: () => {
        this.approveAddingPromise.reject();
      },
      priority: "high",
      texts: { closePopup: appearanceWarning.close },
    };

    const setting: GlobalAlertMessage = {
      popupProps: popupProps,
      alertMessageProps: alertProps,
      closeButton: {
        name: appearanceWarning.close,
        type: "secondary",
        click: () => {
          this.approveAddingPromise.reject();
        },
      },
    };

    return setting;
  };
}

export default withGlobalContext(Appearance);
